import React, { useState } from 'react';
import { Table, Button, Pagination, Icon, Loader, Dimmer } from 'semantic-ui-react';
import styled from 'styled-components';
import { ClaimsData } from '../../../services/dashboard';
import SummaryModal from './SummaryModal';
import { getClaimsReport } from '../../../services/claim';
import {
  dangerNotification,
  warningNotification,
  successNotification
} from '../../../services/notification';
import { ReportModal } from '../../../components/ReportModal';
import moment from 'moment-timezone';

// Estilos
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const TableContainer = styled.div`
  margin-top: 20px;
  overflow-x: auto;
`;

const StyledTable = styled(Table)`
  && {
    font-size: 0.85em;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 30px;
  color: #666;
`;

interface ClaimsTableProps {
  tableData: ClaimsData[];
  tableLoading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalItems: number;
}

const ClaimsTable: React.FC<ClaimsTableProps> = ({
  tableData,
  tableLoading,
  currentPage,
  setCurrentPage,
  totalItems
}) => {
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [selectedEstate, setSelectedEstate] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportState, setReportState] = useState<number | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);

  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (_: any, { activePage }: any) => {
    setCurrentPage(Number(activePage));
  };

  const handleOpenSummaryModal = (estateId: string, estateName: string) => {
    setSelectedEstate({ id: estateId, name: estateName });
    setIsSummaryModalOpen(true);
  };

  const handleCloseSummaryModal = () => {
    setIsSummaryModalOpen(false);
  };

  const handleReportModalClose = () => {
    setIsReportModalOpen(false);
  };

  const handleExportReport = async () => {
    if (!selectedEstate) {
      return;
    }

    setIsExporting(true);

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      const filters = {
        estateId: selectedEstate.id,
        start: startDate,
        end: endDate,
        isRv: true,
        page: 1,
        onlyUnread: false,
        isReport: true
      };

      const status = await getClaimsReport({ filters });

      if (!status) {
        dangerNotification('Ops...', 'Erro ao solicitar relatório de sinistros.');
      } else {
        setReportState(status);
        setIsReportModalOpen(true);
      }
    } catch (err) {
      console.log(err);
      dangerNotification('Ops...', 'Erro ao solicitar relatório de sinistros.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Dimmer active={tableLoading} inverted>
        <Loader>Carregando...</Loader>
      </Dimmer>

      <TableContainer>
        <StyledTable celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Imobiliária</Table.HeaderCell>
              <Table.HeaderCell>Franquia</Table.HeaderCell>
              <Table.HeaderCell>Gestor</Table.HeaderCell>
              <Table.HeaderCell>Abertos</Table.HeaderCell>
              <Table.HeaderCell>Acordo</Table.HeaderCell>
              <Table.HeaderCell>Em Análise</Table.HeaderCell>
              <Table.HeaderCell>Em Andamento</Table.HeaderCell>
              <Table.HeaderCell>Finalizados</Table.HeaderCell>
              <Table.HeaderCell>Total</Table.HeaderCell>
              <Table.HeaderCell>Ações</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {tableLoading ? (
              <Table.Row>
                <Table.Cell colSpan={10} textAlign="center">
                  <Loader active inline="centered" />
                </Table.Cell>
              </Table.Row>
            ) : tableData.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={10}>
                  <NoDataMessage>
                    <Icon name="search" />
                    Nenhum dado encontrado.
                  </NoDataMessage>
                </Table.Cell>
              </Table.Row>
            ) : (
              tableData.map((item, index) => (
                <Table.Row key={`${item.estateId}-${index}`}>
                  <Table.Cell>{item.estateName}</Table.Cell>
                  <Table.Cell>{item.franchiseName}</Table.Cell>
                  <Table.Cell>{item.managerName || 'Não atribuído'}</Table.Cell>
                  <Table.Cell>{item.openCount}</Table.Cell>
                  <Table.Cell>{item.agreementCount}</Table.Cell>
                  <Table.Cell>{item.analysisCount}</Table.Cell>
                  <Table.Cell>{item.inProgressCount}</Table.Cell>
                  <Table.Cell>{item.finishedCount}</Table.Cell>
                  <Table.Cell>{item.totalCount}</Table.Cell>
                  <Table.Cell>
                    <Button
                      icon="chart bar"
                      style={{ backgroundColor: '#59249b', color: '#fff' }}
                      onClick={() =>
                        handleOpenSummaryModal(item.estateId, item.estateName)
                      }
                    ></Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </StyledTable>
      </TableContainer>

      {!tableLoading && totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            activePage={currentPage}
            onPageChange={handlePageChange}
            totalPages={totalPages}
            ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
            firstItem={{ content: <Icon name="angle double left" />, icon: true }}
            lastItem={{ content: <Icon name="angle double right" />, icon: true }}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
          />
        </PaginationContainer>
      )}

      {selectedEstate && (
        <SummaryModal
          isOpen={isSummaryModalOpen}
          onClose={handleCloseSummaryModal}
          title={`Resumo de Sinistros - ${selectedEstate.name}`}
          productType="sinistros"
          estateId={selectedEstate.id}
          onExport={handleExportReport}
        />
      )}

      {isReportModalOpen && (
        <ReportModal
          isOpen={isReportModalOpen}
          header="Relatório de Sinistros"
          message={
            reportState === 200
              ? 'O relatório será gerado em background e será enviado o link para download via e-mail. Atenção, só será possível gerar um novo relatório após a finalização desse.'
              : 'Já existe um relatório semelhante na fila para ser gerado, aguarde a notificação via email.'
          }
          onClose={handleReportModalClose}
        />
      )}
    </>
  );
};

export default ClaimsTable;
